import React, { useCallback } from 'react';
import EkonomiskAnalysKontodelarQuery, {
  IKontodelValue,
} from '../../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Queries/EkonomiskAnalysKontodelarQuery/EkonomiskAnalysKontodelarQuery';
import { IClientAttributeModel } from '../../../../Types/ClientModelTypes';
import { EDAutoCompleteEdit } from '../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
// eslint-disable-next-line aveny/mui-import-rule
import { AutocompleteInputChangeReason } from '@mui/material/Autocomplete';
import GetKontoDelFromOrdningsNr from '../../../../Features/Delsystem/Leverantor/Rutiner/Utils/GetKontoDelFromOrdningsNr/GetKontoDelFromOrdningsNr';
import { IKontodel } from '../../../../Features/Delsystem/_Common/Components/KonteringManager/Query/KonteringManagerQuery';

interface IProps {
  id: string;
  label: any;
  onChange: (newValue: string, callback?: () => void) => void;
  value?: string;
  disabled?: boolean;
  className?: string;
  attribute: IClientAttributeModel;
  testId?: string;
}

/**
 * En autocomplete som ger kontodelar som förslag.
 */
const KontodelEdit: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { onChange } = props;
  const startWithKonto_del =
    props.attribute.AttributeName.startsWith('Konto_del');
  const onSuggestionSelected = useCallback(
    (suggestion: IKontodelValue | null | IKontodel[] | IKontodel) => {
      if (!Array.isArray(suggestion)) {
        if (suggestion) {
          if (typeof suggestion === 'string') {
            onChange(suggestion);
          } else if (startWithKonto_del && 'kontodel' in suggestion) {
            onChange(suggestion.kontodel);
          } else {
            onChange(suggestion.id);
          }
        }
      }
    },
    [onChange, startWithKonto_del]
  );

  const handleChange = useCallback(
    (codeLength: number) =>
      (
        event: React.SyntheticEvent<Element, Event>,
        value: string,
        reason: AutocompleteInputChangeReason
      ) => {
        if (reason === 'input' || reason === 'clear') {
          if (value.length <= codeLength || Number.isNaN(+value)) {
            onChange(value);
          }
        }
      },
    [onChange]
  );

  if (startWithKonto_del) {
    const ordningsNr = Number(props.attribute.Properties?.get('Kontodel')) ?? 0;
    return (
      <GetKontoDelFromOrdningsNr ordningsNr={ordningsNr}>
        {(kontodelar, kontoDelDef) => {
          if (!kontodelar) {
            return <>Kunde inte hitta kodplanen</>;
          }

          const value = kontodelar.find((x: IKontodel) => {
            return x.kontodel === props.value;
          });

          return (
            <EDAutoCompleteEdit<IKontodel>
              disableAutoHighlight
              value={value}
              inputValue={props.value}
              label={props.label}
              options={kontodelar}
              getOptionLabel={option => {
                return option.kontodel + ' - ' + option.benamning;
              }}
              onChange={onSuggestionSelected}
              onInputChange={handleChange(
                kontoDelDef?.antalPositioner != null
                  ? kontoDelDef.antalPositioner
                  : 99
              )}
              inputMargin="none"
              fullWidth
              freeSolo
              testId={props.testId}
            />
          );
        }}
      </GetKontoDelFromOrdningsNr>
    );
  }

  return (
    <EkonomiskAnalysKontodelarQuery>
      {kontodelarValues => {
        const kontodel = kontodelarValues[props.attribute.AttributeName];
        if (!kontodel) {
          return <React.Fragment />;
        }
        return (
          <EDAutoCompleteEdit<IKontodelValue>
            value={kontodel.kontodelar.find(
              (x: IKontodelValue) => x.id === props.value
            )}
            inputValue={props.value}
            label={props.label}
            options={kontodel.kontodelar}
            getOptionLabel={option => option.name}
            onChange={onSuggestionSelected}
            onInputChange={handleChange(
              kontodel.codeLength != null ? kontodel.codeLength : 99
            )}
            inputMargin="none"
            fullWidth
            freeSolo
          />
        );
      }}
    </EkonomiskAnalysKontodelarQuery>
  );
};

export default KontodelEdit;
